import { HoofdstukSchema } from '@/api';
import {
  fetchQueryDataHoofdstukkenWithEdits,
  useHoofdstukkenWithOverweging,
} from '@/api/queries/objects';
import {
  Grondslag,
  groupHoofdstukkenByGrondslag,
} from '@/constants/grondslagen';
import { queryClient } from '@/services/queryClient';
import { createQuery } from '@/utils/tanstack-query/createQuery';

export type BesluitGrondslag = {
  data: Grondslag;
  hoofdstukken: BesluitHoofdstuk[];
};

export type BesluitHoofdstuk = {
  data: HoofdstukSchema;
  grondslag: BesluitGrondslag;
  hasVoorschriften: boolean;
  hasOverweging: boolean;
};

const fetchBesluitHoofdstukken = async ({
  besluitId,
}: {
  besluitId: string;
}) => {
  const [voorschriftHoofdstukken, hoofdstukkenWithOverwegingen] =
    await Promise.all([
      fetchQueryDataHoofdstukkenWithEdits({ besluitId }, {}, queryClient),
      queryClient.fetchQuery(
        useHoofdstukkenWithOverweging.getOptions({ besluitId })
      ),
    ]);

  /**
   * Create sets of lineage ids with overwegingen and voorschrift hoofdstukken.
   * Can be used for quick lookups.
   */
  const lineageIdsWithOverweging = new Set(
    hoofdstukkenWithOverwegingen.objects?.map(({ Lineage }) => Lineage) ?? []
  );
  const lineageIdsWithVoorschriften = new Set(
    voorschriftHoofdstukken.map(({ Lineage }) => Lineage)
  );

  /**
   * Merge the voorschrift hoofdstukken and the hoofdstukken with overwegingen into a single list of hoofdstukken.
   */
  const allHoofdstukken: HoofdstukSchema[] = [...voorschriftHoofdstukken];

  /**
   * We fetch the last hoofdstuk in a lineage that has an overweging.
   * Because of this, we need to be careful that we don't overwrite a voorschrift hoofdstuk with another one in the same lineage that has an overweging.
   */
  hoofdstukkenWithOverwegingen.objects?.forEach((hoofdstukWithOverweging) => {
    if (!lineageIdsWithVoorschriften.has(hoofdstukWithOverweging.Lineage)) {
      allHoofdstukken.push(hoofdstukWithOverweging);
    }
  });

  allHoofdstukken.sort((a, b) => a.Nummer - b.Nummer);

  const grondslagen = groupHoofdstukkenByGrondslag(allHoofdstukken);

  return {
    grondslagen,
    lineageIdsWithOverweging,
    lineageIdsWithVoorschriften,
  };
};

export const useViewerBesluitGrondslagen = createQuery({
  key: ['viewer-besluit-hoofdstukken'],
  queryFn: fetchBesluitHoofdstukken,
});
